var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { EXPERIENCE, InsuranceProducts } from "../constants";
import { UnlinkedApplicationStatus, updateStatus, } from "../features/unlinked-application/unlinkedApplicationSlice";
import { completedAutoUnlinkedApplication, completedUnlinkedApplication, sawQuoteResults, sawSavings, } from "../lib/Avo";
import { useDidMount, useEffectOnceWhen } from "rooks";
import { AllIssuers } from "@trellisconnect/types/dist/src/enums/AllIssuers";
import AppBody from "../components/AppBody";
import { CgSpinner } from "react-icons/cg";
import Header from "../components/Header";
import { PHASE } from "../routeConfig";
import { Redirect } from "react-router-dom";
import { SavvyIssuerIcon } from "../components/IssuerIcon";
import classNames from "classnames";
import { getBundleWithMostSavings } from "../utils/getBundleWithMostSavings";
import { getQuoteWithMostSavings } from "../utils/getQuoteWithMostSavings";
import useAppDispatch from "../hooks/useAppDispatch";
import useAppSelector from "../hooks/useAppSelector";
import useEventPublisher from "../hooks/useEventPublisher";
import useFindIssuer from "../features/configuration/useFindIssuer";
import useImpressionType from "../services/savvy/useImpressionType";
import useResultsHeaderText from "../features/search/useResultsHeaderText";
import useSearchResults from "../services/savvy/useSearchResults";
var QuoteLoadingSuccess = React.memo(function QuoteLoadingSuccess(_a) {
    var impressionId = _a.impressionId, quotes = _a.quotes, ads = _a.ads, currentPlan = _a.currentPlan, bundles = _a.bundles, agencyContent = _a.agencyContent;
    var insuranceType = useAppSelector(function (state) { return state.configuration; }).insuranceType;
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var _b = useAppSelector(function (state) { return state.applicant; }), accountLinked = _b.accountLinked, insuranceProduct = _b.insuranceProduct;
    var eventPublisher = useEventPublisher();
    var accountHasData = useAppSelector(function (state) {
        return state.policies.auto.some(function (_a) {
            var vehicles = _a.vehicles;
            return vehicles.length > 0;
        });
    });
    var impressionType = useImpressionType();
    /**
     * A prefill user who has a complete application will jump straight to this page.
     * In this case we want to set their application status to complete
     */
    var shouldSetAutoApplicationComplete = useAppSelector(function (state) {
        var isPrefillUser = state.configuration.experience === EXPERIENCE.PREFILL;
        var isApplicationComplete = state.unlinkedApplication.status.auto ===
            UnlinkedApplicationStatus.Complete;
        return isPrefillUser && !isApplicationComplete;
    });
    var dispatch = useAppDispatch();
    var cheapestQuote = getQuoteWithMostSavings(quotes);
    var cheapestBundle = getBundleWithMostSavings(bundles);
    var savings = cheapestQuote && cheapestQuote.averageMonthlySavingsCents
        ? cheapestQuote.averageMonthlySavingsCents / 100
        : cheapestBundle && cheapestBundle.averageMonthlySavingsCents
            ? cheapestBundle.averageMonthlySavingsCents / 100
            : 0;
    var showedCurrentIssuer = ((currentPlan === null || currentPlan === void 0 ? void 0 : currentPlan.issuerName) || "current-missing") ===
        ((cheapestQuote === null || cheapestQuote === void 0 ? void 0 : cheapestQuote.issuerName) || "cheapest-missing");
    var offerListRoute = insuranceProduct === InsuranceProducts.Home
        ? PHASE.HOME_OFFER_LIST
        : PHASE.AUTO_OFFER_LIST;
    var hasAgencyImpression = agencyContent !== undefined;
    var recommendExperienceRoute = hasAgencyImpression
        ? PHASE.AGENCY_FULFILLMENT
        : offerListRoute;
    var nextRoute = recommendExperienceRoute;
    useDidMount(function () {
        eventPublisher.loadOffers(impressionId, quotes, ads, {
            accountHasData: accountHasData,
            accountLinked: accountLinked.auto,
        });
    });
    useDidMount(function () {
        var _a, _b, _c;
        if (savings > 0) {
            sawSavings(__assign(__assign({}, commonAnalyticsProperties), { showedCurrentIssuer: showedCurrentIssuer, impressionId: impressionId, impressionType: impressionType }));
        }
        sawQuoteResults(__assign({ insuranceType: insuranceType, sawSavings: savings > 0, showedCurrentIssuer: showedCurrentIssuer, amountOfSavingsCents: savings * 100, autoIssuerShownFirst: cheapestQuote
                ? cheapestQuote.issuerId || cheapestQuote.issuerName
                : cheapestBundle
                    ? ((_a = cheapestBundle.auto) === null || _a === void 0 ? void 0 : _a.issuerId) || ((_b = cheapestBundle.auto) === null || _b === void 0 ? void 0 : _b.issuerName)
                    : "NO_QUOTE", numberOfAutoQuotes: quotes.length, quoteProviderShownFirst: cheapestQuote
                ? cheapestQuote.providerName ||
                    cheapestQuote.issuerName ||
                    "UNKNOWN_PROVIDER"
                : cheapestBundle
                    ? ((_c = cheapestBundle.auto) === null || _c === void 0 ? void 0 : _c.issuerName) || "UNKNOWN_PROVIDER"
                    : "NO_QUOTE", adsAvailable: ads.length > 0 }, commonAnalyticsProperties));
    });
    useEffectOnceWhen(function () {
        dispatch(updateStatus({ auto: UnlinkedApplicationStatus.Complete }));
        completedUnlinkedApplication(commonAnalyticsProperties);
        completedAutoUnlinkedApplication(commonAnalyticsProperties);
    }, shouldSetAutoApplicationComplete);
    return React.createElement(Redirect, { push: true, to: nextRoute });
}, function (prevProps, nextProps) { return prevProps.impressionId === nextProps.impressionId; });
var loadingStateBrands = [
    { issuerId: AllIssuers.ALLSTATE },
    { issuerId: AllIssuers.PROGRESSIVE },
    { issuerId: AllIssuers.GEICO },
    { issuerId: AllIssuers.LIBERTY_MUTUAL },
    { issuerId: AllIssuers.FARMERS },
];
var MAX_TIME_TO_SEARCH_IN_MS = 300000;
var FINISHED_LOADING_DELAY_MS = 1500;
export default function QuoteLoadingRoute() {
    var _a = React.useState(function () { return false; }), progressBarReady = _a[0], setProgressBarReady = _a[1];
    var _b = React.useState(false), timedOut = _b[0], setTimedOut = _b[1];
    var timeoutID = React.useRef();
    var _c = React.useState(0), progress = _c[0], setProgress = _c[1];
    var findIssuer = useFindIssuer();
    var shouldPostUnlinkedAccount = useAppSelector(function (_a) {
        var unlinkedApplication = _a.unlinkedApplication, applicant = _a.applicant;
        return (applicant.didCreateNewPrefillAccountReferenceId &&
            ((unlinkedApplication.prefill.auto.data &&
                !unlinkedApplication.didPostCompleteApplication.auto) ||
                (unlinkedApplication.prefill.home.data &&
                    !unlinkedApplication.didPostCompleteApplication.home)));
    });
    var _d = useSearchResults({ shouldPostUnlinkedAccount: shouldPostUnlinkedAccount }).searchQuery, isSuccess = _d.isSuccess, isError = _d.isError, data = _d.data, error = _d.error;
    var headerText = useResultsHeaderText();
    var uiReady = progressBarReady;
    var isQuoteLoaded = isSuccess && data && uiReady;
    var resultsReady = isSuccess || isError;
    var handleProgressBarFinish = React.useCallback(function () { return setProgressBarReady(true); }, []);
    React.useEffect(function () {
        if (!resultsReady) {
            setTimeout(function () {
                setProgress(85);
            });
        }
        else {
            setProgress(100);
            setTimeout(function () {
                handleProgressBarFinish();
            }, FINISHED_LOADING_DELAY_MS);
        }
    }, [resultsReady, handleProgressBarFinish]);
    useDidMount(function () {
        timeoutID.current = window.setTimeout(function () {
            setTimedOut(true);
        }, MAX_TIME_TO_SEARCH_IN_MS);
    });
    useEffectOnceWhen(function () {
        window.clearTimeout(timeoutID === null || timeoutID === void 0 ? void 0 : timeoutID.current);
    }, isSuccess);
    if (timedOut) {
        throw new Error("Error: Search timed out");
    }
    if (isError) {
        throw new Error(error.data);
    }
    if (isQuoteLoaded) {
        return (React.createElement(QuoteLoadingSuccess, { ads: data.ads, quotes: data.quotes, impressionId: data.impressionId, currentPlan: data.currentPlan, bundles: data.bundles, agencyContent: data.agencyContent }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { showDropShadow: true },
            React.createElement("div", { className: "text-center" },
                React.createElement("div", { className: "text-xs text-black-300" }, "Your policy"),
                React.createElement("div", { className: "text-xs font-semibold text-black-100" }, headerText))),
        React.createElement(AppBody, null,
            React.createElement("div", { className: "overflow-y-auto" },
                React.createElement("div", { className: "m-4 space-y-2" },
                    React.createElement("div", { className: "relative mt-4" },
                        React.createElement("div", { className: "mb-4 flex h-2 overflow-hidden rounded bg-white-300 text-xs" },
                            React.createElement("div", { style: { width: "".concat(progress, "%") }, className: classNames("flex flex-col justify-center whitespace-nowrap rounded bg-[#3572E2] transition-[width]", { "duration-[25000ms] ease-out": !resultsReady }, { "w-full duration-1000 ease-in-out": resultsReady }) }))),
                    React.createElement("div", null,
                        React.createElement("h2", { className: "text-center text-2xl font-bold text-black-100" }, "Checking for recommendations..."),
                        React.createElement("p", { className: "mb-4 mt-2 text-center text-sm text-black-300" },
                            "This will take ",
                            React.createElement("strong", null, "one minute or less."))),
                    loadingStateBrands
                        .map(function (_a) {
                        var issuerId = _a.issuerId;
                        return findIssuer(issuerId);
                    })
                        .filter(function (issuer) { return issuer !== undefined; })
                        .map(function (_a) {
                        var id = _a.id, name = _a.name;
                        return (React.createElement("div", { key: id, className: "flex items-center justify-between rounded-md border border-[#DBDBDB] bg-white p-4" },
                            React.createElement("div", { className: "flex items-center" },
                                React.createElement("div", { className: "max-w-10 mr-2 h-10 max-h-10 w-10" },
                                    React.createElement(SavvyIssuerIcon, { issuerId: id, className: "rounded-full" })),
                                React.createElement("p", { className: "text-base font-bold" }, name)),
                            React.createElement(CgSpinner, { className: "h-6 w-6 animate-spin", color: "#DBDBDB" })));
                    }))))));
}
